import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import BlogRecommendations from '../../../components/feature/BlogRecommendations';
import BlogRelatedStrip from '../../../components/feature/BlogRelatedStrip';
import BlogProgressBar from '../../../components/feature/BlogProgressBar';
import BlogTableOfContents from '../../../components/feature/BlogTableOfContents';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

export default function WholeLifeInsuranceIndianapolisBlog() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Whole Life Insurance Indianapolis: Permanent Coverage Guide 2025';
    
    const setMeta = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    setMeta('description', 'Complete guide to whole life insurance in Indianapolis. Permanent coverage, cash value growth, costs & whether whole life is right for your Indiana family.');
    setMeta('keywords', 'whole life insurance Indianapolis, permanent life insurance Indiana, cash value life insurance, whole life rates Indianapolis');
    setMeta('last-modified', new Date().toISOString());
    setMeta('og:title', 'Whole Life Insurance Indianapolis: Permanent Coverage Guide 2025');
    setMeta('og:description', 'Complete guide to whole life insurance in Indianapolis. Learn about permanent coverage, cash value growth, costs, and whether whole life is right for your Indiana family.');
    setMeta('og:url', `${SITE_URL}/blog/whole-life-insurance-indianapolis`);
    setMeta('og:type', 'article');
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/2889ba2cc98450ec3cd6a7b15d23248e.jpg');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=Indianapolis%20financial%20advisor%20explaining%20whole%20life%20insurance%20permanent%20coverage%20and%20cash%20value%20growth%20to%20couple%20in%20professional%20office%20warm%20lighting%20trust%20expertise%20clean%20background&width=1200&height=630&seq=og-whole-life-indy&orientation=landscape');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog/whole-life-insurance-indianapolis`);

    // Inject Schema.org JSON-LD
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Whole Life Insurance Indianapolis: Build Wealth While You Protect',
        description: 'Discover how whole life insurance in Indianapolis provides lifetime protection plus cash value growth. Compare policies, understand benefits, and get expert guidance.',
        author: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          url: SITE_URL
        },
        publisher: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          url: SITE_URL
        },
        datePublished: '2025-01-12',
        dateModified: '2025-03-23T00:00:00Z',
        url: `${SITE_URL}/blog/whole-life-insurance-indianapolis`,
        mainEntityOfPage: `${SITE_URL}/blog/whole-life-insurance-indianapolis`,
        image: { '@type': 'ImageObject', url: 'https://readdy.ai/api/search-image?query=Indianapolis%20financial%20advisor%20explaining%20whole%20life%20insurance%20permanent%20coverage%20and%20cash%20value%20growth%20to%20couple%20in%20professional%20office%20warm%20lighting%20trust%20expertise%20clean%20background&width=1200&height=630&seq=og-whole-life-indy&orientation=landscape', width: 1200, height: 630 },
        timeRequired: 'PT10M',
        wordCount: 2000,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', 'details summary']
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Hoosier Life Insurance',
        url: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
        description: 'Independent life insurance agency serving Indianapolis and all of Indiana. Licensed specialists helping families find affordable coverage from 20+ top-rated carriers.',
        areaServed: { '@type': 'State', name: 'Indiana' },
        knowsAbout: ['Whole Life Insurance Indianapolis', 'Permanent Life Insurance Indiana', 'Cash Value Life Insurance', 'IUL Indianapolis', 'Life Insurance Wealth Building Indiana']
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How much does whole life insurance cost in Indianapolis?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'A healthy 35-year-old Indianapolis resident pays approximately $420/month (male) or $375/month (female) for $500,000 of whole life insurance. Whole life costs 8-10x more than term life for the same death benefit because it builds cash value and provides lifetime coverage.'
            }
          },
          {
            '@type': 'Question',
            name: 'Is whole life insurance a good investment for Indianapolis families?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Whole life insurance is not primarily an investment — it\'s permanent life insurance with a savings component. Cash value grows at 2-4% annually, which is lower than stock market returns but provides guaranteed, tax-deferred growth. It\'s best for estate planning, generational wealth transfer, and Indianapolis families who have already maxed out 401(k) and IRA contributions.'
            }
          },
          {
            '@type': 'Question',
            name: 'Should I choose term or whole life insurance in Indianapolis?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'For most Indianapolis families with mortgages and children, term life insurance is the better choice — it\'s 8-10x cheaper and provides high coverage amounts during critical years. Whole life makes sense for high-net-worth families focused on estate planning, wealth transfer, or supplementing retirement income with tax-free policy loans.'
            }
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
          { '@type': 'ListItem', position: 3, name: 'Whole Life Insurance Indianapolis', item: `${SITE_URL}/blog/whole-life-insurance-indianapolis` }
        ]
      }
    ];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.id = `schema-blog-whole-life-${index}`;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-blog-whole-life-${index}`);
        if (script) script.remove();
      });
    };
  }, []);

  return (
    <>
      <title>Whole Life Insurance Indianapolis: Permanent Coverage Guide 2026 | Hoosier Life</title>
      <meta name="description" content="Complete guide to whole life insurance in Indianapolis — permanent coverage, cash value growth, 2026 costs by age, and whether whole life is right for your Indiana family." />
      <link rel="canonical" href={`${SITE_URL}/blog/whole-life-insurance-indianapolis`} />
      <meta name="robots" content="index, follow" />
      <div className="min-h-screen bg-white">
        <BlogProgressBar />
        <Header />

        <nav aria-label="Breadcrumb" className="bg-[#1A3A52] border-b border-[#1A3A52]/20">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
            <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-[#D97642] transition-colors">
              <i className="ri-home-4-line text-base"></i>
              <span>Home</span>
            </Link>
            <i className="ri-arrow-right-s-line text-white/40"></i>
            <Link to="/blog" className="text-white/70 hover:text-[#D97642] transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line text-white/40"></i>
            <span className="text-[#D97642] font-semibold">Whole Life Insurance Indianapolis</span>
          </div>
        </nav>

        <article className="max-w-4xl mx-auto px-6 py-16">
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Whole Life Insurance Indianapolis: Permanent Coverage Guide 2025
            </h1>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-8">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line text-[#D97642]"></i>
                Updated March 2025
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line text-[#D97642]"></i>
                10 min read
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-map-pin-line text-[#D97642]"></i>
                Indianapolis, IN
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-shield-check-line text-[#D97642]"></i>
                Licensed Indiana Specialists
              </span>
            </div>
            <p className="text-xl text-gray-700 leading-relaxed">
              Everything you need to know about whole life insurance in Indianapolis: how it works, costs, cash value growth, and whether permanent coverage is right for your family.
            </p>
          </header>

          {/* Key Takeaways */}
          <div className="bg-[#1A3A52] text-white rounded-2xl p-7 mb-12">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-fill text-[#D97642] text-xl"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2.5">
              {[
                'Whole life insurance provides lifetime coverage (never expires) plus guaranteed cash value growth — unlike term life, which expires after 10–30 years',
                'A healthy 35-year-old in Indianapolis pays $420/month (male) or $375/month (female) for $500,000 of whole life — about 13× the cost of term',
                'Cash value grows at a guaranteed 2–4% annually, tax-deferred, and can be borrowed against tax-free at any time',
                'Whole life is best for high-net-worth Indianapolis families doing estate planning — not the right fit for most families with young children and mortgages',
                'For most Indianapolis families, a 20-year term policy is the better choice — it frees up money for retirement accounts and other investments',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                  <i className="ri-check-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Table of Contents */}
          <BlogTableOfContents
            title="In This Guide"
            items={[
              { id: 'what-is-whole-life', label: 'What Is Whole Life Insurance?' },
              { id: 'how-whole-life-works', label: 'How Whole Life Insurance Works' },
              { id: 'whole-life-costs', label: 'Whole Life Insurance Costs in Indianapolis' },
              { id: 'whole-life-faq', label: 'Frequently Asked Questions' },
            ]}
          />

          <div className="prose prose-lg max-w-none">
            <h2 id="what-is-whole-life" className="text-3xl font-bold text-gray-900 mb-4 mt-12">What Is Whole Life Insurance?</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Whole life insurance pays a guaranteed death benefit whenever you die — age 45 or age 95 — and simultaneously builds cash value that grows 2–4% annually. It's permanent, unlike term life that expires after a set period. The tradeoff: it costs 8–13× more than term life for the same death benefit.
            </div>
            <p className="text-gray-700 leading-relaxed mb-6">
              Whole life insurance provides <strong>lifetime coverage</strong> with a guaranteed death benefit and a cash value component that grows tax-deferred. Unlike <Link to="/blog/term-life-insurance-indianapolis" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">term life insurance</Link>, which expires after a set period, whole life coverage lasts until age 100+ as long as you pay premiums.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              For Indianapolis families, whole life insurance serves three purposes: permanent death benefit protection, tax-advantaged cash value accumulation, and estate planning. It's significantly more expensive than <Link to="/blog/cheapest-life-insurance-indiana" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">affordable term coverage</Link> but offers benefits that term policies don't provide. Unsure which to choose? Our <Link to="/blog/term-vs-whole-life-indianapolis" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">term vs. whole life comparison</Link> breaks down the decision for Indianapolis families.
            </p>

            <div className="bg-[#FDF6F0] border-l-4 border-[#D97642] p-6 my-8 rounded-r-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl"></i>
                Who Should Consider Whole Life?
              </h3>
              <p className="text-gray-800 leading-relaxed">
                <strong>Whole life insurance is ideal for high-net-worth Indianapolis families</strong> who have maxed out other retirement accounts, need estate planning tools, or want to leave a guaranteed legacy. For those interested in market-linked growth with downside protection, <Link to="/blog/indexed-universal-life-indianapolis" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">indexed universal life insurance (IUL)</Link> offers a flexible alternative. Learn how <Link to="/blog/wealth-building-life-insurance-indianapolis" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">Indianapolis families use permanent policies to build tax-free wealth</Link>.
              </p>
            </div>

            <h2 id="how-whole-life-works" className="text-3xl font-bold text-gray-900 mb-4 mt-12">How Whole Life Insurance Works</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Every premium payment splits into two buckets — the cost of insurance (death benefit protection) and cash value accumulation. Cash value earns a guaranteed minimum interest rate plus potential dividends. You can borrow against cash value tax-free at any time without a credit check or repayment schedule.
            </div>
            <p className="text-gray-700 leading-relaxed mb-6">
              When you pay your whole life premium, part goes toward the death benefit and part builds cash value. Here's how the cash value component works:
            </p>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-white border-2 border-[#D97642] rounded-xl p-6">
                <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-line-chart-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Cash Value Growth</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Your cash value grows at a guaranteed rate (typically 2-4% annually) plus potential dividends from the insurance company. Growth is tax-deferred—you don't pay taxes until you withdraw.
                </p>
              </div>

              <div className="bg-white border-2 border-[#D97642] rounded-xl p-6">
                <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-bank-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Policy Loans</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  You can borrow against your cash value at low interest rates (typically 5-8%). Loans don't require credit checks or repayment schedules, but unpaid loans reduce the death benefit.
                </p>
              </div>

              <div className="bg-white border-2 border-[#D97642] rounded-xl p-6">
                <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Guaranteed Death Benefit</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Your beneficiaries receive the full death benefit tax-free, regardless of when you pass away. The death benefit is guaranteed as long as premiums are paid.
                </p>
              </div>

              <div className="bg-white border-2 border-[#D97642] rounded-xl p-6">
                <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-money-dollar-circle-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Dividends (Participating Policies)</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Mutual insurance companies may pay annual dividends based on company performance. You can take dividends as cash, use them to reduce premiums, or reinvest to buy additional coverage.
                </p>
              </div>
            </div>

            <h2 id="whole-life-costs" className="text-3xl font-bold text-gray-900 mb-4 mt-12">Whole Life Insurance Costs in Indianapolis</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Whole life insurance costs 8–10× more than term life for the same death benefit. A healthy 35-year-old Indianapolis male pays $420/month for $500,000 of whole life vs. $32/month for 20-year term. The higher cost builds cash value — but most financial advisors recommend maxing out 401(k) and IRA accounts before choosing whole life.
            </div>
            <p className="text-gray-700 leading-relaxed mb-6">
              Whole life insurance costs 8-10x more than term life for the same death benefit. Here's what Indianapolis residents can expect to pay for $500,000 in coverage:
            </p>

            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-6 py-4 text-left font-bold">Age</th>
                    <th className="px-6 py-4 text-left font-bold">Male (Non-Smoker)</th>
                    <th className="px-6 py-4 text-left font-bold">Female (Non-Smoker)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 30</td>
                    <td className="px-6 py-4 text-gray-700">$380/month</td>
                    <td className="px-6 py-4 text-gray-700">$340/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 35</td>
                    <td className="px-6 py-4 text-gray-700">$420/month</td>
                    <td className="px-6 py-4 text-gray-700">$375/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 40</td>
                    <td className="px-6 py-4 text-gray-700">$480/month</td>
                    <td className="px-6 py-4 text-gray-700">$425/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 45</td>
                    <td className="px-6 py-4 text-gray-700">$550/month</td>
                    <td className="px-6 py-4 text-gray-700">$485/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 50</td>
                    <td className="px-6 py-4 text-gray-700">$640/month</td>
                    <td className="px-6 py-4 text-gray-700">$560/month</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 id="whole-life-faq" className="text-3xl font-bold text-gray-900 mb-6 mt-12">Frequently Asked Questions</h2>
            
            <div className="space-y-6 my-8">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">How much does whole life insurance cost in Indianapolis?</h3>
                <p className="text-gray-700 leading-relaxed">
                  A healthy 35-year-old in Indianapolis can expect to pay $350-$450/month for $500,000 in whole life coverage. Rates are significantly higher than term life because the policy builds cash value and provides lifetime coverage.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Is whole life insurance a good investment?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Whole life insurance is not primarily an investment—it's permanent life insurance with a savings component. Cash value grows at 2-4% annually, which is lower than stock market returns but provides guaranteed, tax-deferred growth. For a full cost comparison, read our <Link to="/blog/term-vs-whole-life-indianapolis" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">term vs. whole life guide</Link>. It's best for estate planning, not wealth accumulation for most families.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Can I cancel my whole life policy and get my money back?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Yes, you can surrender your whole life policy and receive the cash surrender value. However, surrender charges apply in the first 10-15 years, and you'll owe income tax on any gains. It's better to borrow against the cash value if you need funds.
                </p>
              </div>
            </div>

            {/* Author Bio */}
            <div className="border border-gray-200 rounded-2xl p-7 my-10 flex items-start gap-5">
              <div className="w-14 h-14 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
                <i className="ri-shield-user-line text-white text-2xl"></i>
              </div>
              <div>
                <p className="font-bold text-gray-900 text-base mb-1">Written by the Licensed Life Insurance Specialists at Hoosier Life Insurance</p>
                <p className="text-sm text-gray-600 leading-relaxed mb-2">
                  Our team of licensed Indiana life insurance agents helps Indianapolis families evaluate permanent vs. term coverage and find the right fit for their financial goals. We are independent agents — not tied to any single carrier — so we compare 20+ A-rated companies to find your best option. All information is reviewed for accuracy and updated regularly to reflect current Indiana market conditions.
                </p>
                <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Licensed in Indiana</span>
                  <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Independent — 20+ Carriers</span>
                  <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Last reviewed March 2025</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 my-12">
              <h2 className="text-3xl font-bold mb-4">Get a Whole Life Insurance Quote</h2>
              <p className="text-xl text-white/80 mb-6">
                Talk to a licensed Indianapolis agent about permanent coverage options for your family.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/book-appointment" className="px-8 py-4 bg-white text-[#1A3A52] rounded-lg font-bold hover:bg-gray-50 transition-colors whitespace-nowrap">
                  Schedule Consultation
                </Link>
                <a href="tel:3176482983" className="px-8 py-4 bg-[#0F2A3F] text-white rounded-lg font-bold hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap">
                  Call (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </article>

        {/* Mid-Article Quiz CTA */}
        <section className="py-12 bg-gradient-to-br from-orange-50 to-amber-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <i className="ri-question-line text-3xl text-orange-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Not Sure Which Policy Is Right for You?
            </h3>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Take our 2-minute quiz to get a personalized recommendation based on your family's unique needs and budget.
            </p>
            <a
              href="/quiz"
              className="inline-flex items-center gap-2 bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Take the Quiz Now
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </section>

        {/* You May Also Like Widget */}
        <BlogRecommendations topic="whole-life" />

        <BlogRelatedStrip currentSlug="whole-life-insurance-indianapolis" />

        <Footer />
      </div>
    </>
  );
}